import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, User, Loader2, ArrowLeft, Check, X, ShieldCheck } from "lucide-react";
import { api, setSessionToken } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { trackSiteEvent } from "../lib/siteTrack";
import TermsAcceptCheckbox from "./TermsAcceptCheckbox";

/**
 * EmailPasswordAuthNative — Firebase-independent email/password + forgot
 * password flow. Talks directly to the backend `/api/auth/email/*` endpoints
 * so sign-in keeps working even when Firebase is unconfigured.
 *
 * v139 — Two-step signup:
 *   1. User enters name + email + password + confirm password.
 *   2. Backend emails a 6-digit code, we switch to the "verify" mode.
 *   3. User enters the code; on success the same session cookie is set as
 *      before and downstream navigation is identical.
 *
 * v144 — T&C acceptance checkbox on signup + optional `onSuccess` prop so
 * the follow modal can reuse this component without a full page nav.
 *
 * Server sets the same httpOnly `session_token` cookie every other auth path
 * uses, so nothing downstream needs to change.
 */
export default function EmailPasswordAuthNative({
  redirectTo = "/app",
  onSuccess,
  initialMode = "signin",
  onModeChange,
}) {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  // signin | signup | forgot | verify
  // Accept parent aliases: "login" → "signin".
  const normalizeIn = (m) => (m === "login" ? "signin" : m);
  const [mode, setMode] = useState(normalizeIn(initialMode));
  // v145.39 — Keep AuthTabs in sync so switching to the Phone tab
  // preserves whether the user picked Sign in vs Create account.
  useEffect(() => {
    const target = normalizeIn(initialMode);
    if ((target === "signin" || target === "signup") && target !== mode) {
      setMode(target);
    }
  }, [initialMode]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  // v146.12 — Terms acceptance is gated by AuthPage's top-level 13+
  // checkbox; the user can't even reach this form until that's ticked.
  // Kept as a state (defaulted true) so the payload keeps its shape.
  const [terms] = useState(true);
  const [busy, setBusy] = useState(false);
  // OTP verification state
  const [otp, setOtp] = useState("");
  const [intentId, setIntentId] = useState("");
  const [resendIn, setResendIn] = useState(0); // seconds until resend is allowed

  // Countdown for the "Resend code" button (60s cooldown).
  const timerRef = useRef(null);
  useEffect(() => {
    if (resendIn <= 0) return;
    timerRef.current = setInterval(
      () => setResendIn((s) => (s > 0 ? s - 1 : 0)),
      1000
    );
    return () => clearInterval(timerRef.current);
  }, [resendIn]);

  const swapMode = (next) => {
    setMode(next);
    setPassword("");
    setConfirmPassword("");
    setOtp("");
    if (next !== "signup" && next !== "verify") setName("");
    if (next !== "verify") setIntentId("");
    // v145.39 — Bubble mode change to AuthTabs so the sibling Phone
    // form stays on the same Sign-in / Sign-up mode.
    if (next === "signin" || next === "signup") onModeChange?.(next === "signin" ? "login" : next);
    // v145.1 — Admin site analytics: count when a visitor opens the
    // signup form (vs. just landing on the signin tab).
    if (next === "signup") {
      trackSiteEvent("signup_click", { source: "email_auth_tabs" });
    }
  };

  const finishAuth = (data) => {
    if (data?.session_token) setSessionToken(data.session_token);
    if (data?.user) localStorage.removeItem("ref_code");
    if (data?.user) setUser(data.user);
    toast.success(
      `Welcome${data?.user?.name ? `, ${data.user.name.split(" ")[0]}` : ""}!`
    );
    if (onSuccess) {
      onSuccess(data);
      return;
    }
    // v144.43 — QR sales employees are regular Linkola users now.
    // They land on the default post-login destination and can open the
    // QR Sales Panel from the More menu when they need it.
    const target = redirectTo;
    if (typeof window !== "undefined" && window.Capacitor?.isNativePlatform?.()) {
      window.location.assign(target);
    } else {
      navigate(target, { replace: true });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // ─── Forgot password ────────────────────────────────────────────────
      if (mode === "forgot") {
        if (!email.trim()) {
          toast.error("Enter your email first");
          return;
        }
        await api.post("/auth/email/reset-request", { email: email.trim() });
        toast.success("If that email is registered, a reset link is on the way.");
        swapMode("signin");
        return;
      }

      // ─── Sign in ─────────────────────────────────────────────────────────
      if (mode === "signin") {
        try {
          const { data } = await api.post("/auth/email/login", {
            email: email.trim(),
            password,
          });
          finishAuth(data);
        } catch (err) {
          // v145.33 — Admin-created email accounts don't have a
          // password yet. The backend returns 409 with a structured
          // detail so we can swap to the OTP-signup flow and let the
          // user set their password + verify their email. The signup
          // endpoint attaches the password to the SAME user_id so any
          // Bio Hub blocks the admin pre-configured stay linked.
          const d = err?.response?.data?.detail;
          if (err?.response?.status === 409 && d && typeof d === "object" && d.code === "needs_setup") {
            toast.info("This account was set up by an admin — set your password to activate it.");
            if (d.name) setName(d.name);
            if (d.email) setEmail(d.email);
            swapMode("signup");
            return;
          }
          throw err;
        }
        return;
      }

      // ─── Sign up — Step 1: request OTP ──────────────────────────────────
      if (mode === "signup") {
        // v146.11 — Name is optional at signup; asked in onboarding step 1.
        if (password.length < 8) {
          toast.error("Password must be at least 8 characters");
          return;
        }
        if (password !== confirmPassword) {
          toast.error("Passwords don't match — check for typos.");
          return;
        }
        const { data } = await api.post("/auth/email/signup/request", {
          email: email.trim(),
          password,
          confirm_password: confirmPassword,
          referral_code: localStorage.getItem("ref_code") || undefined,
          // v146.78 — Vertical landing attribution. Reads `utm_campaign`
          // from the current URL (populated by /for/{vertical} CTAs) so
          // the admin dashboard can compute signups per vertical.
          signup_source: (new URLSearchParams(window.location.search).get("utm_campaign")) || undefined,
        });
        setIntentId(data.intent_id);
        setResendIn(data.resend_available_in_seconds || 60);
        setMode("verify");
        toast.success("Check your inbox — we just emailed you a 6-digit code.");
        return;
      }

      // ─── Sign up — Step 2: verify OTP ───────────────────────────────────
      if (mode === "verify") {
        if (otp.length !== 6) {
          toast.error("Enter the 6-digit code from your email");
          return;
        }
        const { data } = await api.post("/auth/email/signup/verify", {
          intent_id: intentId,
          code: otp,
        });
        finishAuth(data);
        return;
      }
    } catch (err) {
      toast.error(formatDetail(err));
    } finally {
      setBusy(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendIn > 0 || !intentId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/email/signup/resend", {
        intent_id: intentId,
      });
      setResendIn(data.resend_available_in_seconds || 60);
      setOtp("");
      toast.success("Sent — check your inbox.");
    } catch (err) {
      toast.error(formatDetail(err));
    } finally {
      setBusy(false);
    }
  };

  // Password match indicator (only shown once both fields have content)
  const pwFilled = password.length >= 8 && confirmPassword.length > 0;
  const pwMatch = pwFilled && password === confirmPassword;

  // ─── Verify screen ─────────────────────────────────────────────────────
  if (mode === "verify") {
    return (
      <form
        onSubmit={submit}
        className="w-full space-y-3"
        data-testid="email-auth-native-form"
      >
        <button
          type="button"
          onClick={() => swapMode("signup")}
          data-testid="email-native-verify-back"
          className="inline-flex items-center gap-1 text-xs font-bold text-ink/70 hover:underline"
        >
          <ArrowLeft className="w-3 h-3" /> Change email
        </button>
        <div className="text-center py-2">
          <div className="inline-flex items-center justify-center w-12 h-12 border-2 border-ink bg-mint mb-2">
            <ShieldCheck className="w-6 h-6" />
          </div>
          <h3 className="font-black text-base">Check your email</h3>
          <p className="text-xs text-ink/70 mt-1">
            We sent a 6-digit code to{" "}
            <span className="font-bold text-ink">{email}</span>
          </p>
        </div>
        <div>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={6}
            value={otp}
            onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="Enter 6-digit code"
            data-testid="email-native-otp-input"
            autoComplete="one-time-code"
            required
            className="w-full border-2 border-ink px-3 py-3 text-lg font-mono font-black text-center tracking-[0.4em] bg-white"
          />
        </div>
        <button
          type="submit"
          disabled={busy || otp.length !== 6}
          data-testid="email-native-submit-verify"
          className="w-full border-2 border-ink bg-coral text-white px-4 py-3 font-black text-base shadow-brutalSm inline-flex items-center justify-center gap-2 hover:translate-y-[1px] disabled:opacity-60"
        >
          {busy && <Loader2 className="w-3 h-3 animate-spin" />}
          Verify & create account
        </button>
        <div className="text-center text-[11px] font-bold pt-1">
          Didn&apos;t get it?{" "}
          <button
            type="button"
            onClick={handleResendOtp}
            disabled={resendIn > 0 || busy}
            data-testid="email-native-resend-otp"
            className="underline hover:text-coral disabled:no-underline disabled:opacity-50"
          >
            {resendIn > 0 ? `Resend in ${resendIn}s` : "Resend code"}
          </button>
        </div>
      </form>
    );
  }

  // ─── Default: signin / signup / forgot ─────────────────────────────────
  return (
    <form onSubmit={submit} className="w-full space-y-2" data-testid="email-auth-native-form">
      {mode === "forgot" && (
        <button
          type="button"
          onClick={() => swapMode("signin")}
          data-testid="email-native-back-signin"
          className="inline-flex items-center gap-1 text-xs font-bold text-ink/70 hover:underline"
        >
          <ArrowLeft className="w-3 h-3" /> Back to sign in
        </button>
      )}
      {/* v146.11 — Name input removed; asked in step 1 of onboarding. */}
      <div className="relative">
        <Mail className="w-5 h-5 absolute left-2.5 top-1/2 -translate-y-1/2 opacity-60" />
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          data-testid="email-native-email-input"
          autoComplete="email"
          className="w-full border-2 border-ink pl-10 pr-3 py-3 text-base bg-white"
        />
      </div>
      {mode !== "forgot" && (
        <div className="relative">
          <Lock className="w-5 h-5 absolute left-2.5 top-1/2 -translate-y-1/2 opacity-60" />
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={mode === "signup" ? "Create a password (8+ chars)" : "Password"}
            data-testid="email-native-password-input"
            autoComplete={mode === "signup" ? "new-password" : "current-password"}
            minLength={mode === "signup" ? 8 : 1}
            className="w-full border-2 border-ink pl-10 pr-3 py-3 text-base bg-white"
          />
        </div>
      )}
      {mode === "signup" && (
        <div className="relative">
          <Lock className="w-5 h-5 absolute left-2.5 top-1/2 -translate-y-1/2 opacity-60" />
          <input
            type="password"
            required
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder="Re-enter password"
            data-testid="email-native-confirm-password-input"
            autoComplete="new-password"
            minLength={8}
            className={`w-full border-2 pl-10 pr-10 py-3 text-base bg-white ${
              pwFilled
                ? pwMatch
                  ? "border-emerald-600"
                  : "border-rose-600"
                : "border-ink"
            }`}
          />
          {pwFilled && (
            <span
              className="absolute right-2.5 top-1/2 -translate-y-1/2"
              data-testid={pwMatch ? "email-native-pw-match-ok" : "email-native-pw-match-bad"}
            >
              {pwMatch ? (
                <Check className="w-5 h-5 text-emerald-600" />
              ) : (
                <X className="w-5 h-5 text-rose-600" />
              )}
            </span>
          )}
        </div>
      )}
      {mode === "signup" && pwFilled && !pwMatch && (
        <p className="text-[11px] font-bold text-rose-600 pl-1">
          Passwords don&apos;t match yet — double-check for typos.
        </p>
      )}
      <button
        type="submit"
        disabled={busy || (mode === "signup" && !pwMatch)}
        data-testid={`email-native-submit-${mode}`}
        className="w-full border-2 border-ink bg-coral text-white px-4 py-3 font-black text-base shadow-brutalSm inline-flex items-center justify-center gap-2 hover:translate-y-[1px] disabled:opacity-60"
      >
        {busy && <Loader2 className="w-3 h-3 animate-spin" />}
        {mode === "signin" && "Sign in with email"}
        {mode === "signup" && "Send verification code"}
        {mode === "forgot" && "Send reset link"}
      </button>
      {mode === "signin" && (
        <div className="flex justify-between items-center pt-1 gap-2">
          <button
            type="button"
            onClick={() => swapMode("signup")}
            data-testid="email-native-goto-signup"
            className="text-sm font-bold text-ink/80 hover:underline hover:text-ink text-left"
          >
            New here? <span className="text-ink font-black">Create an account</span>
          </button>
          <button
            type="button"
            onClick={() => swapMode("forgot")}
            data-testid="email-native-goto-forgot"
            className="text-xs font-bold text-ink/70 hover:underline hover:text-ink whitespace-nowrap"
          >
            Forgot password?
          </button>
        </div>
      )}
      {mode === "signup" && (
        <p className="text-sm font-bold pt-1 text-center">
          Already have an account?{" "}
          <button
            type="button"
            onClick={() => swapMode("signin")}
            data-testid="email-native-goto-signin"
            className="text-ink font-black underline hover:text-coral"
          >
            Sign in
          </button>
        </p>
      )}
    </form>
  );
}

// FastAPI validation errors return an array of {msg,...} objects, not a
// string. Render safely.
function formatDetail(err) {
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : ""))
      .filter(Boolean)
      .join(" ") || "Check your inputs and try again.";
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  return "Something went wrong. Try again.";
}
